import { Sound, UUID } from '@benzinga/session';
import type { ScannerProtos } from './scanner_protos';
import { UserSettingsFeed } from '@benzinga/user-settings';
import { FilterObject } from '@benzinga/quotes-v3-fields-manager';
import { TableParameters } from '@benzinga/ag-grid-utils';
import {
  GptSearchItem,
  HoldingSearchItem,
  isGptSearchItem,
  isHoldingSearchItem,
  isLinkingSearchItem,
  isSymbolSearchItem,
  isVerticalSearchItem,
  isWatchlistSearchItem,
  LinkingSearchItem,
  SearchItem,
  SymbolSearchItem,
  VerticalSearchItem,
  WatchlistSearchItem,
} from '@benzinga/search-modules';

export type NotifyMethods = 'voice' | 'sound' | 'desktop';

export enum RefreshInterval {
  '10s' = 10,
  '30s' = 30,
  '1m' = 60,
  'Never' = -1,
  'Real-time' = -2,
}

export const REFRESH_INTERVALS = [
  {
    key: RefreshInterval['10s'],
    label: '10s',
  },
  {
    key: RefreshInterval['30s'],
    label: '30s',
  },
  {
    key: RefreshInterval['1m'],
    label: '1m',
  },
  {
    key: RefreshInterval['Never'],
    label: 'Never',
  },
  {
    key: RefreshInterval['Real-time'],
    label: 'Real-time',
  },
];

export interface ScannerNotificationConfig {
  enabled: boolean;
  soundId?: Sound; // One of the id's in SoundAlert (defined in global.ts)
  trigger: 'add';

  type: NotifyMethods;
  // To keep things simple, this is a flat structure for now.
  voiceTemplate?: string;
}

export interface ColumnConfigV1 {
  name: string;
  width?: number;
}

export interface FilterIngress {
  isCustom?: boolean;
  field: ScannerProtos.DataField;
  operator: string;
  parameters: (number | null)[] | string[];
}

export type SortDir = ScannerProtos.SortDir;

export type DataField<T extends '-dropdown' | '' = '-dropdown' | ''> = {
  category: string;
  columnable: boolean;
  description: string;
  options?: string;
  filterable: boolean;
  format:
    | 'boolean'
    | 'flag'
    | 'link'
    | 'note'
    | 'period'
    | 'priceAlert'
    | 'progress'
    | 'set'
    | 'sparkLine'
    | 'symbol'
    | 'text'
    | 'topics'
    | 'calculatedChangePercent'
    | 'change'
    | 'changePercent'
    | 'changeShortenedNumber'
    | 'commaSeparatedNumber'
    | 'date'
    | 'dateTime'
    | 'number'
    | 'positiveNumber'
    | 'percentNumber'
    | 'positivePercentNumber'
    | 'positivePrice'
    | 'price'
    | `shortenedNumber${T}`
    | 'percentNumberBackgroundColor'
    | 'time'
    | 'dropdown';
  label: string;
  labelShort: string;
  name: string;
  permissions: string;
  updateFrequency: string;
  filterOptions?: Record<string, string> | null | undefined;
};

export type DataFieldIngress = {
  quoteFields: (Omit<DataField, 'filterOptions'> & { filterOptions: string | null | undefined })[];
  quoteFieldOptions: Record<string, Record<string, string>>;
};

export type ScannerSource = 'stocks' | 'crypto';
export interface ScannerConfigLegacy {
  name?: string;
  uuid?: UUID;
  columns: ColumnConfigV1[];
  filters: FilterObject[];
  hideFiltersBar?: boolean;
  hideToolbar?: boolean;
  filtersBarCollapsed?: boolean;
  limit: number;
  notifications?: ScannerNotificationConfig[];
  refreshInterval: number;
  sortDir: ScannerProtos.SortDir;
  sortField?: string;
  source: 'stocks' | 'crypto';
  stopColumnHighlighting?: boolean;
}

export type ScannerSearchItem =
  | SymbolSearchItem
  | WatchlistSearchItem
  | GptSearchItem
  | LinkingSearchItem
  | HoldingSearchItem
  | VerticalSearchItem;

export const isScannerSearchItem = (item: SearchItem): item is ScannerSearchItem =>
  (isSymbolSearchItem(item) ||
    isWatchlistSearchItem(item) ||
    isLinkingSearchItem(item) ||
    isGptSearchItem(item) ||
    isHoldingSearchItem(item) ||
    isVerticalSearchItem(item)) ??
  false;

export interface ScannerConfigV1 {
  name?: string;
  uuid?: UUID;
  isFavorite?: boolean;
  description?: string;
  columns: ColumnConfigV1[];
  filters: FilterObject[];
  limit: number;
  notifications?: ScannerNotificationConfig[];
  refreshInterval: number;
  sortDir: SortDir;
  sortField?: string;
  source: ScannerSource;
}

export type ScannerVersionV1 = 1;
export interface ScannerUserSettingV1 {
  version: ScannerVersionV1;
  configs: ScannerConfigV1[];
}

export type ScannerConfigV2 = Omit<ScannerConfigV1, 'columns'> & { columns: ColumnConfigV1[] };
export type ScannerVersionV2 = 2;
export interface ScannerUserSettingV2 {
  version: ScannerVersionV2;
  configs: ScannerConfigV2[];
}

export type ScannerConfigV3 = Omit<ScannerConfigV2, 'columns'> & {
  tableParameters: TableParameters;
  tags: ScannerSearchItem[];
};
export type ScannerVersionV3 = 3;
export interface ScannerUserSettingV3 {
  version: ScannerVersionV3;
  configs: ScannerConfigV3[];
}

export type AllScannerUserSetting = ScannerUserSettingV1 | ScannerUserSettingV2 | ScannerUserSettingV3;

export type ScannerVersion = ScannerVersionV3;
export type ScannerConfig = ScannerConfigV3;
export type ScannerUserSetting = ScannerUserSettingV3;
export type ScannerSettingsFeed = UserSettingsFeed<
  'scanner',
  ScannerVersion,
  ScannerUserSetting,
  AllScannerUserSetting
>;
